import { memo } from 'react';

import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { isEmpty } from 'remeda';
import useSWRMutation from 'swr/mutation';

import { PartnerSpotlightCard } from '../partnerSpotlightCard/PartnerSpotlightCard';

import { PARTNER_SPOTLIGHT_STATUSES_LABELS } from './PartnerSpotlightStatusGroup.constants';

import css from './PartnerSpotlightStatusGroup.module.css';

import { partnerSpotlightService } from 'v2/api/partnerSpotlightService';
import { SortableCard } from 'v2/components/card/sortableCard/SortableCard';
import { PartnerSpotlightStatusesEnum } from 'v2/constants';
import { useSortableList } from 'v2/hooks/useSortableList';
import { PartnerSpotlightModel } from 'v2/types/PartnerSpotlightModel';

interface PartnerSpotlightStatusGroupProps {
  status: PartnerSpotlightStatusesEnum;
  partnerSpotlights: PartnerSpotlightModel[];
  onRevalidate: VoidFunction;
  dragDisabled: boolean;
  hideWhenEmpty: boolean;
}

export const PartnerSpotlightStatusGroup = memo<PartnerSpotlightStatusGroupProps>(
  ({ status, partnerSpotlights, onRevalidate, dragDisabled, hideWhenEmpty }) => {
    const { items, itemIds, sensors, activeItem, onDragStart, onDragEnd, onDragCancel } =
      useSortableList<PartnerSpotlightModel>(partnerSpotlights, {
        onReorder: (next) => {
          trigger({
            status,
            places: next.map((item, index) => ({ id: item.id, place: index + 1 })),
          });
        },
      });

    const { trigger } = useSWRMutation(
      `changePartnerSpotlightsOrder-${status}`,
      partnerSpotlightService.changePartnerSpotlightsOrder,
      {
        onSuccess: () => {
          onRevalidate();
        },
      }
    );

    if (isEmpty(items)) {
      if (hideWhenEmpty) {
        return null;
      }

      return (
        <div className={css.partnerSpotlightStatusGroup}>
          <h3 className={css.partnerSpotlightStatusGroupTitle}>{PARTNER_SPOTLIGHT_STATUSES_LABELS[status]}</h3>
          <div className={css.partnerSpotlightStatusGroupEmpty}>No partner spotlights in this category</div>
        </div>
      );
    }

    const cardList = (
      <div className={css.partnerSpotlightStatusGroupCards}>
        {items.map((item) =>
          dragDisabled ? (
            <div key={item.id} className={css.partnerSpotlightStatusGroupCardSlot}>
              <PartnerSpotlightCard partnerSpotlight={item} onRevalidate={onRevalidate} />
            </div>
          ) : (
            <SortableCard key={item.id} id={item.id} className={css.partnerSpotlightStatusGroupCardSlot}>
              <PartnerSpotlightCard partnerSpotlight={item} onRevalidate={onRevalidate} />
            </SortableCard>
          )
        )}
      </div>
    );

    return (
      <div className={css.partnerSpotlightStatusGroup}>
        <h3 className={css.partnerSpotlightStatusGroupTitle}>{PARTNER_SPOTLIGHT_STATUSES_LABELS[status]}</h3>

        {dragDisabled ? (
          cardList
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={onDragCancel}
          >
            <SortableContext items={itemIds} strategy={horizontalListSortingStrategy}>
              {cardList}
            </SortableContext>

            <DragOverlay adjustScale style={{ cursor: 'grabbing' }}>
              {activeItem && (
                <div className={css.partnerSpotlightStatusGroupCardSlot}>
                  <PartnerSpotlightCard partnerSpotlight={activeItem} onRevalidate={onRevalidate} />
                </div>
              )}
            </DragOverlay>
          </DndContext>
        )}
      </div>
    );
  }
);

PartnerSpotlightStatusGroup.displayName = 'PartnerSpotlightStatusGroup';
